import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

export default function MultiSelect({ values = [], onChange, options, placeholder = "Select…", testid, maxLabel = 2 }) {
  const [open, setOpen] = useState(false);
  const selected = options.filter((o) => values.includes(o.value));

  const toggle = (val) => {
    if (values.includes(val)) onChange(values.filter((v) => v !== val));
    else onChange([...values, val]);
  };

  const remove = (val, e) => { e.stopPropagation(); onChange(values.filter((v) => v !== val)); };

  const label = () => {
    if (selected.length === 0) return placeholder;
    if (selected.length <= maxLabel) return selected.map((s) => s.label).join(", ");
    return `${selected.length} selected`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          data-testid={testid}
          className={cn("w-full justify-between font-normal min-h-9 h-auto py-1.5 flex-wrap gap-1", selected.length === 0 && "text-slate-500")}
        >
          <div className="flex flex-wrap gap-1 items-center">
            {selected.length === 0 && <span>{placeholder}</span>}
            {selected.length > 0 && selected.length <= maxLabel && selected.map((s) => (
              <span key={s.value} className="inline-flex items-center gap-1 rounded bg-blue-50 border border-blue-200 text-blue-800 px-1.5 py-0.5 text-xs">
                {s.label}
                <X className="h-3 w-3 cursor-pointer" onClick={(e) => remove(s.value, e)} />
              </span>
            ))}
            {selected.length > maxLabel && <span className="text-slate-700">{label()}</span>}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Search…" data-testid={testid ? testid + "-search" : undefined} />
          <CommandList>
            <CommandEmpty>No results.</CommandEmpty>
            <CommandGroup>
              {options.map((o) => {
                const active = values.includes(o.value);
                return (
                  <CommandItem
                    key={o.value}
                    value={o.label}
                    onSelect={() => toggle(o.value)}
                    data-testid={testid ? `${testid}-option-${o.value}` : undefined}
                  >
                    <Check className={cn("mr-2 h-4 w-4", active ? "opacity-100" : "opacity-0")} />
                    {o.label}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
